import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, AlertTriangle, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate } from "../lib/api";
import { REGISTERS } from "../lib/registers";
import { PageHeader } from "../components/ModulePage";
import { QrScanner } from "../components/QrScanner";
import { RagBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { NewIssueDialog } from "../components/WorkOrders";

export default function Scan() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [issue, setIssue] = useState(false);

  const lookup = useCallback(async (c) => {
    setScanning(false);
    try {
      const r = await api.get("/lookup", { params: { code: c } });
      setRes(r.data);
    } catch (e) {
      setRes(null);
      toast.error(fmtErr(e));
    }
  }, []);

  const item = res?.item;
  return (
    <div className="mx-auto max-w-xl">
      <PageHeader eyebrow="Mobile" title="Scan asset / chemical" description="Scan an asset QR label or a chemical container barcode." />
      {scanning ? <QrScanner onResult={lookup} /> : (
        <Button onClick={() => { setRes(null); setScanning(true); }} className="h-14 w-full bg-rose-600 text-base hover:bg-rose-700" data-testid="start-scan-btn"><ScanLine className="mr-2 h-5 w-5" />Open camera scanner</Button>
      )}
      <form onSubmit={(e) => { e.preventDefault(); lookup(code); }} className="mt-4 flex gap-2">
        <input className="field" placeholder="Or type asset ID / barcode / CAS" value={code} onChange={(e) => setCode(e.target.value)} data-testid="manual-code-input" />
        <Button type="submit" variant="outline" className="h-11 border-slate-700 bg-transparent" data-testid="manual-lookup-btn">Find</Button>
      </form>
      {res?.type === "asset" && (
        <div className="panel fade-up mt-6 space-y-3 p-5" data-testid="scan-result-asset">
          <div className="flex items-center justify-between"><span className="font-mono text-cyan-300">{item.asset_code}</span><RagBadge status={item.rag} /></div>
          <p className="font-display text-2xl font-bold uppercase">{item.name}</p>
          <p className="text-sm text-slate-400">{REGISTERS[item.kind]?.title} - {item.location || "-"} - next due {fmtDate(item.next_due)}</p>
          <div className="grid gap-2">
            {(REGISTERS[item.kind]?.templates || []).map((t) => (
              <Button key={t} onClick={() => nav(`/inspect/${t}?asset=${item.id}`)} className="tap bg-slate-800 hover:bg-slate-700" data-testid={`scan-inspect-${t}`}><ClipboardCheck className="mr-2 h-4 w-4" />Start {t.replace(/_/g, " ")}</Button>
            ))}
            <Button onClick={() => setIssue(true)} className="tap bg-rose-600 hover:bg-rose-700" data-testid="scan-report-defect-btn"><AlertTriangle className="mr-2 h-4 w-4" />Report defect</Button>
          </div>
        </div>
      )}
      {res?.type === "area" && (
        <div className="panel fade-up mt-6 space-y-3 p-5" data-testid="scan-result-area">
          <span className="font-mono text-cyan-300">{item.code}</span>
          <p className="font-display text-2xl font-bold uppercase">{item.name}</p>
          <p className="text-sm text-slate-400">Area check-in label. Start a pathway that includes this area:</p>
          {res.pathways.map((p) => <Button key={p.id} onClick={() => nav(`/pathway/${p.id}`)} className="tap w-full bg-rose-600 hover:bg-rose-700" data-testid={`scan-start-pathway-${p.id}`}>Start {p.name}</Button>)}
          {!res.pathways.length && <p className="text-xs text-slate-500">No pathways include this area yet.</p>}
        </div>
      )}
      {res?.type === "chemical" && (
        <div className="panel fade-up mt-6 space-y-3 p-5" data-testid="scan-result-chemical">
          <div className="flex items-center justify-between"><span className="font-mono text-cyan-300">{item.cas_number}</span><RagBadge status={item.rag} /></div>
          <p className="font-display text-2xl font-bold uppercase">{item.product_name}</p>
          <p className="text-sm text-slate-400">{item.storage_area} - {item.current_quantity}/{item.max_quantity} {item.unit}</p>
          <div className="flex flex-wrap gap-1">{item.flags.map((f, i) => <RagBadge key={i} status={f.level} label={f.label} />)}</div>
        </div>
      )}
      <NewIssueDialog open={issue} onOpenChange={setIssue} preset={item && res?.type === "asset" ? { asset_id: item.id, asset_name: item.name, location: item.location, title: `Defect: ${item.name}` } : null} onCreated={() => setRes(null)} />
    </div>
  );
}
